import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, CalendarDays, MapPin, Users } from "lucide-react";
import { API } from "@/lib/api";
import { DualPrice } from "@/components/DualPrice";

const fmtDate = (d) => {
  if (!d) return "";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch {
    return d;
  }
};

export const Tours = () => {
  const [tours, setTours] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/tours`)
      .then((res) => setTours(res.data.tours || []))
      .catch(() => setTours([]));
  }, []);

  if (!tours || tours.length === 0) return null;

  // a journey becomes a Past Event automatically the day after it ends
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const endsAt = (t) => (t.departure_date || t.arrival_date ? new Date(`${t.departure_date || t.arrival_date}T23:59:59`) : null);
  const upcoming = tours.filter((t) => !endsAt(t) || endsAt(t) >= now);
  const past = tours.filter((t) => endsAt(t) && endsAt(t) < now);
  if (!upcoming.length && !past.length) return null;

  const card = (t, i, isPast) => (
    <motion.div
      key={t.public_token}
      data-testid={`${isPast ? "past-tour-card" : "tour-card"}-${i}`}
      className={`group flex flex-col overflow-hidden border border-amber-500/15 bg-[#101014] transition-colors hover:border-amber-500/40 ${isPast ? "opacity-60" : ""}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: isPast ? 0.6 : 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: i * 0.08 }}
    >
      {t.cover_image && (
        <div className="relative h-44 overflow-hidden">
          <img
            src={t.cover_image}
            alt={t.name}
            loading="lazy"
            className={`h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${isPast ? "grayscale" : ""}`}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#101014] via-transparent to-transparent" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-serif text-2xl font-light text-neutral-50 [text-wrap:balance]">{t.name}</h3>
        <div className="mt-3 space-y-1.5 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
          {(t.arrival_date || t.departure_date) && (
            <p className="flex items-center gap-2">
              <CalendarDays className="h-3.5 w-3.5 text-amber-400/70" />
              {fmtDate(t.arrival_date)} — {fmtDate(t.departure_date)}
            </p>
          )}
          {t.location && (
            <p className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-amber-400/70" />
              {t.location}
            </p>
          )}
          {!isPast && t.spots_left !== null && t.spots_left !== undefined && (
            <p data-testid={`tour-card-spots-${i}`} className="flex items-center gap-2">
              <Users className="h-3.5 w-3.5 text-amber-400/70" />
              {t.spots_left > 0 ? `${t.spots_left} places left` : "Sold out"}
            </p>
          )}
          {isPast && <p className="flex items-center gap-2 text-neutral-500">Past event</p>}
        </div>
        <div className="mt-auto flex items-center justify-between pt-6">
          {t.ticket_price && (
            <span data-testid={`tour-card-price-${i}`} className="font-luxe text-lg tracking-wide text-amber-100">
              <DualPrice value={t.ticket_price} />
            </span>
          )}
          <Link
            data-testid={`${isPast ? "past-tour-link" : "tour-card-link"}-${i}`}
            to={`/tour/${t.public_token}`}
            className="ml-auto flex items-center gap-2 border border-amber-500/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
          >
            {isPast ? "See the journey" : "Reserve"} <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </motion.div>
  );

  return (
    <section data-testid="tours-section" className="border-t border-amber-500/10 bg-[#0a0a0d] px-5 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl">
        {upcoming.length > 0 && (
          <>
            <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-300/80">Open for booking</p>
            <h2 className="mt-4 font-luxe text-3xl uppercase tracking-[0.12em] text-neutral-50 sm:text-4xl">Upcoming Journeys</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-400">
              Ticketed departures anyone can join — reserve your place and travel on your own schedule.
            </p>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{upcoming.map((t, i) => card(t, i, false))}</div>
          </>
        )}
        {past.length > 0 && (
          <div className={upcoming.length ? "mt-16 border-t border-neutral-800/60 pt-12" : ""}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-neutral-500">The archive</p>
            <h2 data-testid="past-events-heading" className="mt-4 font-luxe text-2xl uppercase tracking-[0.12em] text-neutral-400 sm:text-3xl">Past Events</h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{past.map((t, i) => card(t, i, true))}</div>
          </div>
        )}
      </div>
    </section>
  );
};
